import React, { useCallback } from 'react';
import { FunctionComponent } from 'react';
import { FieldRenderProps, Validator } from '../../Field';
import { InputProps } from '../../Components/Input';
import { TextField } from '../Text';

export const validateUrl: Validator<string> = async (url: string) => {
    if (!url || typeof url !== 'string') {
        return { pass: false, message: 'Invalid website' };
    }

    if (url.split(' ').length > 1) {
        return { pass: false, message: 'Cannot contain a space' };
    }

    if (url.endsWith('.')) {
        return { pass: false, message: 'Cannot end with a dot' };
    }

    if (!url.includes('.')) {
        return { pass: false, message: 'Must include a top level domain like .com or .co.uk' };
    }

    try {
        const withProtocol =
            url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}}`;
        new URL(withProtocol);
    } catch (err) {
        return { pass: false, message: 'Invalid website' };
    }

    return { pass: true };
};

type Compose = (value: string, otherValidator?: Validator<string>) => ReturnType<Validator<string>>;
const composeValidators: Compose = async (value: string, otherValidator?: Validator<string>) => {
    const result = await validateUrl(value);

    if (!result.pass) {
        return result;
    }

    if (otherValidator) {
        return otherValidator(value);
    }

    return { pass: true };
};

type Props = {
    label: string;
    description?: string;
    initialValue?: string;
};

type AllowedFieldProps = Omit<FieldRenderProps<string>, 'memoChild' | 'children' | 'initialValue'>;

type AllowedInputProps = Omit<
    InputProps,
    'label' | 'name' | 'onChange' | 'onBlur' | 'value' | 'defaultValue'
>;

export type WebsiteFieldProps = AllowedFieldProps & AllowedInputProps & Props;

export const WebsiteField: FunctionComponent<WebsiteFieldProps> = ({
    id,
    name,
    label,
    description,
    initialValue,
    onBlurValidate,
    onChangeValidate,
    onSubmitValidate,
    required,
    ...inputProps
}) => {
    const _onBlurValidate = useCallback<Validator<string>>(async (value) => {
        if (value === '') {
            return { pass: true };
        }

        return composeValidators(value, onBlurValidate);
    }, []);

    const _onSubmitValidate = useCallback<Validator<string>>(async (value) => {
        if (value === '') {
            return { pass: true };
        }

        return composeValidators(value, onSubmitValidate);
    }, []);

    const fieldProps = {
        name,
        initialValue,
        onBlurValidate: _onBlurValidate,
        onChangeValidate,
        onSubmitValidate: _onSubmitValidate,
        required,
    };

    return (
        <TextField
            id={id}
            description={description}
            label={label}
            inputMode="url"
            {...fieldProps}
            {...inputProps}
        />
    );
};
